/**
 * Mobile Transaction Submit Button Component
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Submit button with cancel option in edit mode
 */

import { Paper, Button, Stack, CircularProgress } from "@mui/material";

export interface MobileTransactionSubmitButtonProps {
  transactionType: "expense" | "income" | "transfer";
  isEditMode: boolean;
  workspaceSlug: string | undefined;
  isSubmitting: boolean;
  uploadingAttachment: boolean;
  focusedField: "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | `detail:${string}` | null;
  isInitialFocus: boolean;
  addOptionDialogOpen: boolean;
  hasChanges?: boolean; // Whether form has changes (for edit mode)
  onCancel: () => void;
  /** Form id to submit (e.g. "fee-edit-form" for fee edit). Default "transaction-form". */
  submitFormId?: string;
}

export default function MobileTransactionSubmitButton({
  transactionType,
  isEditMode,
  workspaceSlug,
  isSubmitting,
  uploadingAttachment,
  focusedField,
  isInitialFocus,
  addOptionDialogOpen,
  hasChanges = true, // Default to true (enable button) if not provided
  onCancel,
  submitFormId = "transaction-form",
}: MobileTransactionSubmitButtonProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderTop: 1,
        borderColor: "divider",
        px: 2,
        py: 1.5,
        position: "fixed",
        left: 0,
        right: 0,
        bottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
        zIndex: 1400,
        bgcolor: "background.paper",
        visibility: (focusedField || isInitialFocus || addOptionDialogOpen) ? "hidden" : "visible",
        opacity: (focusedField || isInitialFocus || addOptionDialogOpen) ? 0 : 1,
        transition: "opacity 0.2s ease, visibility 0.2s ease",
        pointerEvents: (focusedField || isInitialFocus || addOptionDialogOpen) ? "none" : "auto",
      }}
    >
      <Stack direction="row" spacing={2}>
        {isEditMode && (
          <Button
            fullWidth
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting || uploadingAttachment}
            sx={{
              height: "48px",
              minHeight: "48px",
            }}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          form={submitFormId}
          fullWidth
          variant="contained"
          color={transactionType === "income" ? "success" : transactionType === "transfer" ? "primary" : "error"}
          size="large"
          disabled={isSubmitting || uploadingAttachment || (isEditMode && !hasChanges)}
          onClick={(e) => {
            // Ensure click works on mobile
            if (isSubmitting || uploadingAttachment) {
              e.preventDefault();
              return;
            }
            // Let form submission handle it
          }}
          onTouchStart={(e) => {
            // Ensure touch events work on mobile
            e.stopPropagation();
          }}
          onTouchEnd={(e) => {
            // Ensure touch events work on mobile
            e.stopPropagation();
          }}
          sx={{
            height: "48px",
            minHeight: "48px",
            touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : isEditMode ? "Update" : "Create"}
        </Button>
      </Stack>
    </Paper>
  );
}
